import React from "react";
import { useTranslation } from "react-i18next";
import {
    Code2,
    Sparkles,
    Eye,
    Package,
    Paperclip,
    Brain,
    ToyBrick,
    MessageCircle,
} from "lucide-react";

const AnimatedEmptyState = ({
    title,
    subtitle,
    titleColor = "text-gray-700",
    isGenerating = false,
}) => {
    const { t } = useTranslation();

    return (
        <div className="h-full bg-white">
            <div className="flex items-center justify-center h-full min-h-[600px]">
                <div className="text-center text-gray-500 space-y-8 max-w-md mx-auto px-4 pt-8">
                    <div className="space-y-4">
                        <div className="flex justify-center">
                            <div className="relative">
                                <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl flex items-center justify-center animate-pulse shadow-lg">
                                    <Code2 className="h-10 w-10 text-blue-500" />
                                </div>
                                <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center animate-bounce shadow-md">
                                    <Sparkles className="h-3 w-3 text-white" />
                                </div>
                                {/* Floating particles */}
                                <div className="absolute -top-2 -left-2 w-2 h-2 bg-blue-400 rounded-full animate-ping opacity-75"></div>
                                <div
                                    className="absolute -bottom-2 -right-2 w-2 h-2 bg-purple-400 rounded-full animate-ping opacity-75"
                                    style={{ animationDelay: "1s" }}
                                ></div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <h3
                                className={`text-xl font-semibold ${titleColor}`}
                            >
                                {title}
                            </h3>
                            <p className="text-gray-500 text-sm leading-relaxed">
                                {subtitle}
                            </p>
                        </div>
                    </div>

                    {/* Animated Features List - Endless Loop */}
                    <div className="h-48 overflow-hidden relative">
                        <style jsx>{`
                            @keyframes infiniteScroll {
                                0% {
                                    transform: translateY(0);
                                }
                                100% {
                                    transform: translateY(-50%);
                                }
                            }
                            .animate-infinite-scroll {
                                animation: infiniteScroll 12s linear infinite;
                            }
                            .icon-pulse {
                                animation: pulse 2s infinite;
                            }
                            @keyframes pulse {
                                0%,
                                100% {
                                    transform: scale(1);
                                }
                                50% {
                                    transform: scale(1.1);
                                }
                            }
                        `}</style>

                        <div className="animate-infinite-scroll space-y-3">
                            {/* First set of features */}
                            <div className="flex items-center gap-3 p-3 rounded-xl border border-blue-200 bg-blue-50 hover:scale-105 transition-transform duration-200">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-100 text-blue-600 icon-pulse">
                                    <MessageCircle className="h-4 w-4" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                    {t("stepsExecutionPanel.chatAssistant")}
                                </span>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-xl border border-purple-200 bg-purple-50 hover:scale-105 transition-transform duration-200">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-purple-100 text-purple-600 icon-pulse">
                                    <Package className="h-4 w-4" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                    {t(
                                        "stepsExecutionPanel.blueprintTemplates"
                                    )}
                                </span>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-xl border border-yellow-200 bg-yellow-50 hover:scale-105 transition-transform duration-200">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-yellow-100 text-yellow-600 icon-pulse">
                                    <Brain className="h-4 w-4" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                    {t("stepsExecutionPanel.skillsAutomation")}
                                </span>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-xl border border-green-200 bg-green-50 hover:scale-105 transition-transform duration-200">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-green-100 text-green-600 icon-pulse">
                                    <ToyBrick className="h-4 w-4" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                    {t("stepsExecutionPanel.integrationMenu")}
                                </span>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-xl border border-orange-200 bg-orange-50 hover:scale-105 transition-transform duration-200">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-orange-100 text-orange-600 icon-pulse">
                                    <Paperclip className="h-4 w-4" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                    {t("stepsExecutionPanel.attachFiles")}
                                </span>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-xl border border-pink-200 bg-pink-50 hover:scale-105 transition-transform duration-200">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-pink-100 text-pink-600 icon-pulse">
                                    <Eye className="h-4 w-4" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                    {t("stepsExecutionPanel.instantPreview")}
                                </span>
                            </div>

                            {/* Duplicate set for seamless loop */}
                            <div className="flex items-center gap-3 p-3 rounded-xl border border-blue-200 bg-blue-50 hover:scale-105 transition-transform duration-200">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-100 text-blue-600 icon-pulse">
                                    <MessageCircle className="h-4 w-4" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                    {t("stepsExecutionPanel.chatAssistant")}
                                </span>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-xl border border-purple-200 bg-purple-50 hover:scale-105 transition-transform duration-200">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-purple-100 text-purple-600 icon-pulse">
                                    <Package className="h-4 w-4" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                    {t(
                                        "stepsExecutionPanel.blueprintTemplates"
                                    )}
                                </span>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-xl border border-yellow-200 bg-yellow-50 hover:scale-105 transition-transform duration-200">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-yellow-100 text-yellow-600 icon-pulse">
                                    <Brain className="h-4 w-4" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                    {t("stepsExecutionPanel.skillsAutomation")}
                                </span>
                            </div>
                        </div>

                        {/* Gradient fade overlays */}
                        <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-white to-transparent pointer-events-none z-10"></div>
                        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none z-10"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnimatedEmptyState;
